import { useRequest } from 'ahooks';
import { getPlatformData } from './service';
import { Button, Drawer, DrawerProps, message, Tooltip } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import { initEcharts } from 'lib/echarts';
import { formatNumber } from 'utils/numberFormat';
import { isStoreAliasFirst, useStoreNameConfig } from 'hooks/business/store/useStoreName';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslate } from '@tolgee/react';
import { QuickRangePicker } from 'components/QuickRangePicker';
import { What } from 'views/Aide/Scenario/PolicyTable';
import styled from '@emotion/styled';

function ShopeeTrendDrawer({
  storePerfData,
  onCheckDetail,
  ...otherProps
}: DrawerProps & {
  storePerfData: Dashboard.PlatformData.StorePerfData;
  onCheckDetail: (data: Dashboard.PlatformData.StorePerfData) => void;
}) {
  const { region, storeId, storeName, storeAlias } = storePerfData;

  const { t } = useTranslate();

  const { config } = useStoreNameConfig();

  const name = isStoreAliasFirst(config, 'SHOPEE', region) ? storeAlias || storeName : storeName;

  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(7, 'day').startOf('day'),
    dayjs().subtract(1, 'day').endOf('day'),
  ]);

  const { loading, data } = useRequest(
    () =>
      getPlatformData({
        storeId: storeId,
        startTime: dateRange[0].format('YYYY-MM-DD HH:mm:ss'),
        endTime: dateRange[1].format('YYYY-MM-DD HH:mm:ss'),
      }).then((res) => {
        return res
          .map(({ data, ...others }) => {
            return {
              ...others,
              data: (JSON.parse(data) as any[]).reduce<any>((res, cur) => {
                res[cur.metric_name] = [cur.current_period, cur.unit];
                return res;
              }, {}),
            };
          })
          .reverse();
      }),
    {
      refreshDeps: [storeId, dateRange],
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );

  const chartRef1 = useRef<HTMLDivElement>(null);
  const chartRef2 = useRef<HTMLDivElement>(null);
  const chartRef3 = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!data || !chartRef1.current || !chartRef2.current || !chartRef3.current) {
      return;
    }

    const chart1 = initEcharts(chartRef1.current);
    const chart2 = initEcharts(chartRef2.current);
    const chart3 = initEcharts(chartRef3.current);

    const percentFormatter = (value: any) =>
      typeof value === 'number' ? formatNumber(value / 100, '0.0%') : '-';

    const csatRateList = data.map(({ data }) => data.csat_rate?.[0]);
    const csatRateTargetList = csatRateList.map(() => 70);

    const dateList = data.map(({ countDt }) => countDt);

    const tooltipFormatter = (params: any, target: string) => {
      const date = dayjs(params[0].name);
      const line1 = `<div style="margin-bottom: 2px; color: #515359;">${date.format('MM/DD')} (${
        [
          t('multiTimeSelect.周日'),
          t('multiTimeSelect.周一'),
          t('multiTimeSelect.周二'),
          t('multiTimeSelect.周三'),
          t('multiTimeSelect.周四'),
          t('multiTimeSelect.周五'),
          t('multiTimeSelect.周六'),
        ][date.day()]
      })</div>`;
      const line2 = `<div style="margin-bottom: 4px; font-size: 12px; color: #84868C;">${t(
        '统计区间 : x-y',
        {
          x: date.subtract(31, 'days').format('MM/DD'),
          y: date.subtract(2, 'days').format('MM/DD'),
        },
      )}</div>`;
      const line3 = `<div style="margin-bottom: 1px; display: flex; justify-content: space-between; alignitems: center; font-size: 12px; color: #84868C;"><span>${
        params[0].marker
      } ${
        params[0].seriesName
      }</span> <span style="font-weight: bold; color: #232426;">${percentFormatter(
        params[0].value,
      )}</span></div>`;
      const line4 = `<div style="margin-bottom: 1px; display: flex; justify-content: space-between; alignitems: center; font-size: 12px; color: #84868C;"><span>${params[1].marker} ${params[1].seriesName}</span> <span style="font-weight: bold; color: #232426;">${target}</span></div>`;
      return `<div>${line1}${line2}${line3}${line4}</div>`;
    };

    chart1.setOption({
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => tooltipFormatter(params, '≥70%'),
      },
      legend: {
        selectedMode: false,
        data: [t('好评'), t('目标')],
        top: 0,
        left: 'center',
      },
      grid: {
        left: '2%',
        right: '2%',
        top: '10%',
        bottom: '2%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: dateList,
        axisLabel: {
          formatter: (value: string) => value.slice(5),
        },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 100,
        interval: 20,
        axisLabel: {
          formatter: (num: number) => `${num}%`,
        },
      },
      series: [
        {
          name: t('好评'),
          type: 'line',
          data: csatRateList,
          smooth: true,
          showSymbol: false,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: 'rgb(91, 143, 249)',
          },
          areaStyle: {
            color: 'rgba(91, 143, 249, .3)',
          },
        },
        {
          name: t('目标'),
          type: 'line',
          data: csatRateTargetList,
          smooth: true,
          showSymbol: false,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: 'rgb(246, 189, 22)',
          },
          areaStyle: {
            color: 'rgba(246, 189, 22, .3)',
          },
        },
      ],
    });

    chart2.setOption({
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => tooltipFormatter(params, '≥90%'),
      },
      legend: {
        selectedMode: false,
        data: [t('回复率'), t('目标')],
        top: 0,
        left: 'center',
      },
      grid: {
        left: '2%',
        right: '3%',
        top: '10%',
        bottom: '2%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: dateList,
        axisLabel: {
          formatter: (value: string) => value.slice(5),
        },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 100,
        interval: 20,
        axisLabel: {
          formatter: (num: number) => `${num}%`,
        },
      },
      series: [
        {
          name: t('回复率'),
          type: 'line',
          data: data.map(({ data }) => data.response_rate?.[0]),
          smooth: true,
          showSymbol: false,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: 'rgb(97, 221, 170)',
          },
          areaStyle: {
            color: 'rgba(97, 221, 170, .3)',
          },
        },
        {
          name: t('目标'),
          type: 'line',
          data: data.map((_) => 90),
          smooth: true,
          showSymbol: false,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: 'rgb(246, 189, 22)',
          },
          areaStyle: {
            color: 'rgba(246, 189, 22, .3)',
          },
        },
      ],
    });

    const nonFulfillmentRateList = data.map(({ data }) => data.non_fulfillment_rate?.[0]);

    chart3.setOption({
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => tooltipFormatter(params, '≤10%'),
      },
      legend: {
        selectedMode: false,
        data: [t('未完成率'), t('目标')],
        top: 0,
        left: 'center',
      },
      grid: {
        left: '2%',
        right: '3%',
        top: '10%',
        bottom: '2%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: dateList,
        axisLabel: {
          formatter: (value: string) => value.slice(5),
        },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 100,
        interval: 20,
        axisLabel: {
          formatter: (num: number) => `${num}%`,
        },
      },
      series: [
        {
          name: t('未完成率'),
          type: 'line',
          data: nonFulfillmentRateList,
          smooth: true,
          showSymbol: false,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: 'rgb(114, 98, 253)',
          },
          areaStyle: {
            color: 'rgba(114, 98, 253, .3)',
          },
        },
        {
          name: t('目标'),
          type: 'line',
          data: data.map((_) => 10),
          smooth: true,
          showSymbol: false,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: 'rgb(246, 189, 22)',
          },
          areaStyle: {
            color: 'rgba(246, 189, 22, .3)',
          },
        },
      ],
    });

    const handleResize = () => {
      chart1.resize();
      chart2.resize();
      chart3.resize();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart1.dispose();
      chart2.dispose();
      chart3.dispose();
    };
  }, [data, t]);

  if (!data || !storeId) {
    return <>{loading ? <LoadingOutlined /> : <></>}</>;
  }

  return (
    <Drawer title={`${t('查看趋势')} - ${name}`} width="85vw" {...otherProps}>
      <Container>
        <header>
          <QuickRangePicker
            quickActions={[
              {
                key: 'last7days',
                label: t('remind.近x天', { x: 7 }),
                range: [
                  dayjs().subtract(7, 'day').startOf('day'),
                  dayjs().subtract(1, 'day').endOf('day'),
                ],
              },
              {
                key: 'last30days',
                label: t('remind.近x天', { x: 30 }),
                range: [
                  dayjs().subtract(30, 'day').startOf('day'),
                  dayjs().subtract(1, 'day').endOf('day'),
                ],
              },
            ]}
            value={dateRange}
            onChange={(range) => {
              const [start, end] = range || [];
              if (start && end) {
                setDateRange([start, end]);
              }
            }}
          />
        </header>
        <div>
          <section>
            <header>
              <h3>
                {t('聊天满意度')}
                <Tooltip
                  title={t('会话收到买家“好评”的数量占总评价数（“好评”、“一般”和“差评”）的百分比')}
                >
                  <What />
                </Tooltip>
              </h3>
              <Button
                type="link"
                onClick={(e) => {
                  otherProps.onClose?.(e as any);
                  onCheckDetail(storePerfData);
                }}
              >
                {t('marketing.查看明细')}
              </Button>
            </header>
            <div ref={chartRef1} />
          </section>
          <div>
            <section>
              <header>
                <h3>
                  {t('回复率')}
                  <Tooltip
                    title={t(
                      '卖家在收到新聊天消息和报价后12小时内回复的比例（占总数的百分比），自动回复不计入回复率的计算。',
                    )}
                  >
                    <What />
                  </Tooltip>
                </h3>
              </header>
              <div ref={chartRef2} />
            </section>
            <section>
              <header>
                <h3>
                  {t('未完成率')}
                  <Tooltip title={t('由于卖家原因导致取消或退货退款的订单，占总订单的百分比。')}>
                    <What />
                  </Tooltip>
                </h3>
              </header>
              <div ref={chartRef3} />
            </section>
          </div>
        </div>
      </Container>
    </Drawer>
  );
}

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100% + 48px);
  margin: -24px;
  background: #f7f9fa;
  > header {
    flex: 0 0 auto;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    height: 48px;
    padding: 0 16px;
    background: #fff;
  }
  > div {
    flex: 1 0 0;
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin: 16px;
    > * {
      flex: 1 0 0;
    }
    > div {
      display: flex;
      align-items: stretch;
      gap: 16px;
      > section {
        flex: 1 0 0;
      }
    }
    > section,
    > div > section {
      display: flex;
      flex-direction: column;
      > header {
        flex: 0 0 auto;
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: 56px;
        padding: 0 16px;
        margin-bottom: 1px;
        background: #fff;
        > h3 {
          margin-bottom: 0;
        }
      }
      > div {
        flex: 1 0 0;
        background: #fff;
      }
    }
  }
`;

export default ShopeeTrendDrawer;
